import { useAuth } from "@dl/web-core-js/auth";
import { cn, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, PackageX, Plus, Undo2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CutJobClient, needsShort, type CutJobDetailDTO, type CutJobDTO, type CutJobPanelLineDTO } from "../../api/cutJobs";
import { ShopFloorClient, type ProductionOrderOperationDTO, type StepReportRequest } from "../../api/floor";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import { CutJobDocuments } from "../production/cutjobs/CutJobDocuments";
import { CUT_JOBS_KEY, cutJobTitle } from "../production/cutjobs/cutJobModel";
import { BigButton, QuantityDialog, ScrapDialog } from "./kioskParts";
import { FinishDialog, leftToDo } from "./StationQueue";
import { useCutJobDocuments, ViewDocumentButton } from "./DocumentPreview";

type Flash = (tone: "ok" | "error", text: string) => void;

/** The saw's to-do list: its confirmed and started cut jobs (docs/cutting-jobs §7). */
export function CutJobList({ jobs, current, onPick }: { jobs: CutJobDTO[]; current: number | null; onPick: (id: number) => void }) {
  const { t } = useTranslation();
  if (jobs.length === 0) {
    return <p className="px-4 py-6 text-[15px] text-ink-muted">{t("kiosk.jobs.empty", "No cut job waiting at this station.")}</p>;
  }
  return (
    <ul className="flex max-h-[55vh] flex-col overflow-auto">
      {jobs.map((job) => (
        <li key={job.id} className="border-b border-line last:border-b-0">
          <button
            type="button"
            onClick={() => onPick(job.id)}
            className={cn("flex min-h-16 w-full flex-col gap-0.5 px-4 py-2 text-left hover:bg-surface-sunk", job.id === current && "bg-accent-soft")}
          >
            <span className="flex items-baseline gap-3">
              <span className="text-[19px] font-semibold">{cutJobTitle(job, t)}</span>
              {job.status === "IN_PROGRESS" && <span className="rounded bg-warn-soft px-1.5 text-[12px] text-warn">{t("cut-job.status.in-progress", "In progress")}</span>}
              {job.due_date && <span className="ml-auto text-[14px] text-ink-muted">{job.due_date}</span>}
            </span>
            <span className="text-[14px] text-ink-muted">
              {t("kiosk.jobs.line", "Lots {{lots}} · {{sheets}} sheets · {{done}}/{{required}} pieces", {
                lots: job.lots ?? "",
                sheets: formatNumber(job.sheets_planned ?? 0),
                done: formatNumber(job.pieces_done ?? 0),
                required: formatNumber(job.pieces_required ?? 0),
              })}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * One cut job at the saw (docs/cutting-jobs §7), grouped by panel type: its
 * raw panels (planned from the lots' production BOMs; confirm what was used,
 * scrap a damaged sheet, take back a mistake) and its pieces, lot by lot, each
 * reported as at any step. Nothing of the raw stock is used until it is
 * confirmed here; the job is finished once its panels are confirmed, short if
 * its pieces are not all reported.
 */
export function CutJobStation({
  jobId,
  stationId,
  operatorBadge,
  onReport,
  onFlash,
  onClose,
}: {
  jobId: number;
  stationId: number;
  operatorBadge: string | null;
  /** The kiosk's step report: overrides, the undo, the flash. */
  onReport: (step: ProductionOrderOperationDTO, request: StepReportRequest) => Promise<unknown>;
  onFlash: Flash;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const client = useMemo(() => new CutJobClient(http), [http]);
  const key = [...CUT_JOBS_KEY, "station-job", jobId];
  const detail = useQuery({ queryKey: key, queryFn: () => client.detail(jobId), refetchInterval: 30_000 });
  const [dialog, setDialog] = useState<null | { kind: "use" | "takeback" | "scrap"; line: CutJobPanelLineDTO } | { kind: "piece" | "piece-scrap"; step: ProductionOrderOperationDTO }>(null);
  const floor = useMemo(() => new ShopFloorClient(http), [http]);
  // Finishing the job asks first what was cut of its pieces still open.
  const [finishing, setFinishing] = useState<ProductionOrderOperationDTO[] | null>(null);

  const settle = (next: CutJobDetailDTO) => {
    queryClient.setQueryData(key, next);
    void queryClient.invalidateQueries({ queryKey: ["production"] });
  };
  const fail = (failure: unknown) => onFlash("error", refusalMessage(failure, t("error.generic", "Something went wrong.")));
  const use = useMutation({
    mutationFn: (request: { part_id: number; used?: number; scrapped?: number; production_scrap_reason_id?: number }) =>
      client.use(jobId, { ...request, station_id: stationId, operator_badge: operatorBadge }),
    onSuccess: (next, request) => {
      settle(next);
      onFlash("ok", Number(request.scrapped ?? 0) > 0
        ? t("kiosk.jobs.scrapped", "{{n}} sheets scrapped", { n: formatNumber(request.scrapped) })
        : t("kiosk.jobs.used", "{{n}} sheets used", { n: formatNumber(request.used) }));
    },
    onError: fail,
  });
  const finish = useMutation({
    mutationFn: async () => {
      try {
        return await client.finish(jobId, { station_id: stationId, operator_badge: operatorBadge });
      } catch (failure) {
        const message = needsShort(failure);
        if (message == null) throw failure;
        const yes = await confirm({
          title: t("kiosk.jobs.short.title", "Finish the job short?"),
          message,
          confirmLabel: t("kiosk.jobs.short.confirm", "Finish short"),
          variant: "danger",
        });
        return yes ? client.finish(jobId, { short: true, station_id: stationId, operator_badge: operatorBadge }) : null;
      }
    },
    onSuccess: (next) => {
      if (!next) return;
      settle(next);
      onFlash("ok", t("kiosk.jobs.finished", "Job finished"));
      onClose();
    },
    onError: fail,
  });
  const cutPieces = useMutation({
    mutationFn: (quantities: Record<number, number>) =>
      floor.bulk({
        kind: "FINISH",
        station_id: stationId,
        operator_badge: operatorBadge,
        rows: (finishing ?? []).map((step) => ({ operation_id: step.id, quantity_good: quantities[step.id] ?? 0 })),
      }),
    onSuccess: async (results) => {
      setFinishing(null);
      await queryClient.invalidateQueries({ queryKey: key });
      const refused = results.filter((result) => !result.ok);
      if (refused.length > 0) {
        onFlash("error", t("kiosk.bulk.refused", "{{n}} not done: {{why}}", { n: refused.length, why: refused[0]?.message ?? "" }));
        return;
      }
      finish.mutate();
    },
    onError: fail,
  });
  // The Cut Rite plans uploaded on the job, for the saw (or the foam cutter) to look at.
  const plans = useCutJobDocuments(detail.data?.documents ?? []);
  const reportPiece = async (step: ProductionOrderOperationDTO, request: StepReportRequest) => {
    await onReport(step, request);
    await queryClient.invalidateQueries({ queryKey: key });
  };

  if (!detail.data) {
    return <p className="py-16 text-center text-[18px] text-ink-muted">{t("table.loading", "Loading")}</p>;
  }
  const { job, lots, panels, pieces, piece_panels: piecePanels, documents } = detail.data;
  const busy = use.isPending || finish.isPending || cutPieces.isPending;
  const openPieces = pieces.filter((piece) => piece.status !== "DONE" && piece.status !== "SKIPPED");

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
        <span className="text-[34px] font-bold">{t("cut-job.title", "Cut job {{n}}", { n: job.number })}</span>
        {job.cutrite_number && <span className="rounded-md border-2 border-ink px-3 text-[26px] font-bold">{job.cutrite_number}</span>}
        <ViewDocumentButton label={t("kiosk.view.cut-plan", "View cut plan")} documents={plans} />
        <button type="button" onClick={onClose} className="ml-auto flex items-center gap-1 text-[15px] text-ink-muted underline">
          <X className="size-4" />
          {t("kiosk.jobs.close", "Back to the list")}
        </button>
      </div>
      <p className="text-[16px] text-ink-muted">
        {lots.map((lot) => `${String(lot.number)} ${lot.part_number ?? ""} × ${formatNumber(lot.quantity)}`).join(" · ")}
      </p>
      {job.note && <p className="rounded-lg bg-warn-soft px-4 py-2 text-[17px] text-warn">{job.note}</p>}
      {documents.length > 0 && <CutJobDocuments documents={documents} large />}

      {panels.map((group) => {
        const rows = pieces.filter((piece) => piecePanels[String(piece.id)] === group.virtual_part_id);
        return (
          <section key={group.virtual_part_id} className="rounded-xl border border-line p-4">
            <h3 className="text-[22px] font-semibold">{group.virtual_name}</h3>
            <p className="mb-3 font-mono text-[14px] text-ink-muted">{group.virtual_number}</p>

            <h4 className="mb-2 text-[17px] font-semibold">{t("kiosk.jobs.panels", "Raw panels")}</h4>
            <ul className="flex flex-col gap-2">
              {group.lines.map((line) => {
                const used = Number(line.used);
                const left = Math.max(0, Number(line.planned) - used);
                return (
                  <li key={line.part_id} className="flex flex-wrap items-center gap-3 rounded-lg bg-surface-sunk px-4 py-3">
                    <div className="min-w-[12rem] flex-1">
                      <p className="text-[18px] font-semibold">{line.number}</p>
                      <p className="text-[14px] text-ink-muted">{line.name}</p>
                    </div>
                    <span className="text-[16px] tabular-nums">
                      {t("kiosk.jobs.panel-figures", "Planned {{planned}} · used {{used}} · scrapped {{scrapped}}", {
                        planned: formatNumber(line.planned),
                        used: formatNumber(line.used),
                        scrapped: formatNumber(line.scrapped),
                      })}
                    </span>
                    <div className="flex gap-2">
                      <IconButton label={t("kiosk.jobs.use", "Used")} icon={<Plus className="size-5" />} primary disabled={busy}
                        onClick={() => setDialog({ kind: "use", line })} />
                      <IconButton label={t("kiosk.jobs.scrap", "Scrap")} icon={<PackageX className="size-5" />} disabled={busy}
                        onClick={() => setDialog({ kind: "scrap", line })} />
                      {used > 0 && (
                        <IconButton label={t("kiosk.jobs.takeback", "Take back")} icon={<Undo2 className="size-5" />} disabled={busy}
                          onClick={() => setDialog({ kind: "takeback", line })} />
                      )}
                    </div>
                    {left > 0 && used > 0 && <span className="w-full text-[13px] text-ink-muted">{t("kiosk.jobs.left", "{{n}} left of the plan", { n: formatNumber(left) })}</span>}
                  </li>
                );
              })}
            </ul>

            <h4 className="mb-2 mt-4 text-[17px] font-semibold">{t("kiosk.jobs.pieces", "Pieces to cut")}</h4>
            {rows.length === 0 ? (
              <p className="text-[15px] text-ink-muted">{t("kiosk.jobs.no-pieces", "No piece yet: the lots are not released.")}</p>
            ) : (
              <ul className="flex flex-col divide-y divide-line rounded-lg border border-line">
                {rows.map((step) => {
                  const finished = step.status === "DONE" || step.status === "SKIPPED";
                  return (
                    <li key={step.id} className={cn("flex flex-wrap items-center gap-3 px-3 py-2", finished && "opacity-60")}>
                      <span className="w-28 text-[17px] font-semibold tabular-nums">{step.component_code}</span>
                      <span className="w-12 text-[24px] font-bold">{step.letter}</span>
                      <span className="min-w-0 flex-1 truncate text-[14px] text-ink-muted">{step.part_number}</span>
                      <span className="text-[18px] tabular-nums">
                        {formatNumber(step.quantity_done ?? 0)}/{formatNumber(step.quantity_required_counted ?? step.quantity_required)}
                      </span>
                      {Number(step.quantity_scrap ?? 0) > 0 && (
                        <span className="rounded bg-warn-soft px-1.5 text-[13px] text-warn">{t("cut-job.scrap-n", "{{n}} scrap", { n: formatNumber(step.quantity_scrap) })}</span>
                      )}
                      {!finished && (
                        <div className="flex gap-2">
                          <IconButton label={t("kiosk.report", "Report")} icon={<Plus className="size-5" />} primary
                            onClick={() => setDialog({ kind: "piece", step })} />
                          <IconButton label={t("kiosk.scrap", "Scrap")} icon={<PackageX className="size-5" />}
                            onClick={() => setDialog({ kind: "piece-scrap", step })} />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}

      <div className="grid gap-3 sm:grid-cols-2">
        <BigButton icon={<CheckCircle2 className="size-6" />} label={t("kiosk.jobs.finish", "Finish the job")} primary
          disabled={busy} onClick={() => (openPieces.length > 0 ? setFinishing(openPieces) : finish.mutate())} />
      </div>

      {finishing && (
        <FinishDialog rows={finishing} busy={cutPieces.isPending} onClose={() => setFinishing(null)}
          onSubmit={(quantities) => cutPieces.mutate(quantities)} />
      )}
      {dialog?.kind === "use" && (
        <QuantityDialog
          title={t("kiosk.jobs.use.title", "{{panel}}: sheets used", { panel: dialog.line.number })}
          initial={Math.max(0, Number(dialog.line.planned) - Number(dialog.line.used))}
          max={Math.max(0, Number(dialog.line.planned) - Number(dialog.line.used))}
          onClose={() => setDialog(null)}
          onSubmit={(value) => {
            const line = dialog.line;
            setDialog(null);
            if (value > 0) use.mutate({ part_id: line.part_id, used: value });
          }}
        />
      )}
      {dialog?.kind === "takeback" && (
        <QuantityDialog
          title={t("kiosk.jobs.takeback.title", "{{panel}}: sheets to take back", { panel: dialog.line.number })}
          hint={t("kiosk.jobs.takeback.hint", "Confirmed as used by mistake: they go back to stock.")}
          initial={1}
          max={Number(dialog.line.used)}
          onClose={() => setDialog(null)}
          onSubmit={(value) => {
            const line = dialog.line;
            setDialog(null);
            if (value > 0) use.mutate({ part_id: line.part_id, used: -Math.min(value, Number(line.used)) });
          }}
        />
      )}
      {dialog?.kind === "scrap" && (
        <ScrapDialog
          title={t("kiosk.jobs.scrap.title", "{{panel}}: sheets scrapped", { panel: dialog.line.number })}
          onClose={() => setDialog(null)}
          onSubmit={(quantity, reasonId) => {
            const line = dialog.line;
            setDialog(null);
            use.mutate({ part_id: line.part_id, scrapped: quantity, production_scrap_reason_id: reasonId });
          }}
        />
      )}
      {dialog?.kind === "piece" && (
        <QuantityDialog
          title={t("kiosk.jobs.piece.title", "{{code}} {{letter}}: good pieces cut", { code: dialog.step.component_code ?? "", letter: dialog.step.letter ?? "" })}
          initial={leftToDo(dialog.step)}
          max={leftToDo(dialog.step)}
          onClose={() => setDialog(null)}
          onSubmit={(value) => {
            const step = dialog.step;
            setDialog(null);
            if (value !== 0) void reportPiece(step, { kind: "REPORT", quantity_good: value });
          }}
        />
      )}
      {dialog?.kind === "piece-scrap" && (
        <ScrapDialog
          onClose={() => setDialog(null)}
          onSubmit={(quantity, reasonId) => {
            const step = dialog.step;
            setDialog(null);
            void reportPiece(step, { kind: "REPORT", quantity_good: 0, quantity_scrap: quantity, production_scrap_reason_id: reasonId });
          }}
        />
      )}
    </div>
  );
}

function IconButton({ label, icon, onClick, disabled, primary }: { label: string; icon: React.ReactNode; onClick: () => void; disabled?: boolean; primary?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex min-h-12 items-center gap-2 rounded-lg border-2 px-4 text-[16px] font-semibold disabled:opacity-40",
        primary ? "border-accent bg-accent text-ink-on-accent hover:opacity-90" : "border-line bg-surface hover:bg-surface-sunk",
      )}
    >
      {icon}
      {label}
    </button>
  );
}
